import { DangerousQueryDialog } from './DangerousQueryDialog';
import { ExternalFileChangeDialog } from './ExternalFileChangeDialog';
import { ResultsEditPreviewDialog } from './ResultsEditPreviewDialog';
import { AiChatCliRequiredDialog } from '../../../ai-chat/components/AiChatCliRequiredDialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '../../../../components/ui/alert-dialog';
import type { DangerousQueryResult } from '../../../../core/engines';
import type { ExternalFileChange } from '../hooks/useExternalFileChange';
import type { PendingUpdate, TransactionMode, TransactionState } from '../../core/types/workspaceTypes';
import type { EditableColumnInfo, EditableTableInfo } from '../utils/editabilityDetection';

type QueryEditorTabDialogsProps = {
  // Dangerous-query confirmation
  dangerInfo: DangerousQueryResult | null;
  dangerousDialogOpen: boolean;
  handleDangerousConfirm: () => void;
  handleDangerousCancel: () => void;
  // External file change prompt
  isActive: boolean;
  pendingChange: ExternalFileChange | null;
  filePath: string | undefined;
  isFileDirty: boolean;
  handleExternalKeep: () => void;
  handleExternalReload: () => void;
  // Transaction-guarded database switch
  pendingDatabaseChange: string | null;
  setPendingDatabaseChange: (value: string | null) => void;
  handleConfirmDatabaseChange: () => void;
  // Results edit preview
  connectionId: string | undefined;
  tabDatabase: string | undefined;
  isPreviewDialogOpen: boolean;
  setIsPreviewDialogOpen: (open: boolean) => void;
  resultsEditing: {
    pendingChanges: PendingUpdate[];
    editability: {
      editableColumns: Map<string, EditableColumnInfo>;
      editableTables: Map<string, EditableTableInfo>;
    };
    saveChanges: () => Promise<void>;
  };
  handleOpenInQueryEditor: (query: string) => void;
  transactionMode: TransactionMode | undefined;
  transactionState: TransactionState | undefined;
  // AI chat CLI requirement
  showCliRequiredDialog: boolean;
  /** T13 — the task launcher's own CLI-required dialog (separate hook instance). */
  aiTaskCliRequiredOpen?: boolean;
  setAiTaskCliRequiredOpen?: (open: boolean) => void;
  setShowCliRequiredDialog: (open: boolean) => void;
};

/**
 * The dialog layer of a query editor tab: dangerous-query confirmation,
 * external-file-change prompt, transaction-guarded database-switch warning,
 * results-edit preview, and the AI-chat CLI-required dialog. Pure JSX moved
 * verbatim from QueryEditorTabContent — all state and handlers stay with the
 * host's hooks.
 */
export function QueryEditorTabDialogs({
  dangerInfo,
  dangerousDialogOpen,
  handleDangerousConfirm,
  handleDangerousCancel,
  isActive,
  pendingChange,
  filePath,
  isFileDirty,
  handleExternalKeep,
  handleExternalReload,
  pendingDatabaseChange,
  setPendingDatabaseChange,
  handleConfirmDatabaseChange,
  connectionId,
  tabDatabase,
  isPreviewDialogOpen,
  setIsPreviewDialogOpen,
  resultsEditing,
  handleOpenInQueryEditor,
  transactionMode,
  transactionState,
  showCliRequiredDialog,
  aiTaskCliRequiredOpen,
  setAiTaskCliRequiredOpen,
  setShowCliRequiredDialog,
}: QueryEditorTabDialogsProps) {
  return (
    <>
      {/* Dangerous Query Confirmation Dialog */}
      {dangerInfo && (
        <DangerousQueryDialog
          open={dangerousDialogOpen}
          onConfirm={handleDangerousConfirm}
          onCancel={handleDangerousCancel}
          dangerInfo={dangerInfo}
        />
      )}

      {/* External file change prompt — shown only when this tab is active */}
      {isActive && pendingChange && filePath && (
        <ExternalFileChangeDialog
          open
          filePath={filePath}
          kind={pendingChange.kind}
          hasUnsavedChanges={isFileDirty}
          onKeep={handleExternalKeep}
          onReload={handleExternalReload}
        />
      )}

      {/* Database Switch with Active Transaction Warning */}
      <AlertDialog open={pendingDatabaseChange !== null} onOpenChange={(open) => !open && setPendingDatabaseChange(null)}>
        <AlertDialogContent data-testid="transaction-database-switch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Active Transaction</AlertDialogTitle>
            <AlertDialogDescription>
              Switching databases will rollback the active transaction. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingDatabaseChange(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDatabaseChange}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Results Edit Preview Dialog */}
      {connectionId && tabDatabase && (
        <ResultsEditPreviewDialog
          open={isPreviewDialogOpen}
          onOpenChange={setIsPreviewDialogOpen}
          pendingChanges={resultsEditing.pendingChanges}
          editableColumns={resultsEditing.editability.editableColumns}
          editableTables={resultsEditing.editability.editableTables}
          connectionId={connectionId}
          database={tabDatabase}
          onExecute={resultsEditing.saveChanges}
          onOpenInQueryEditor={handleOpenInQueryEditor}
          transactionMode={transactionMode}
          transactionState={transactionState}
        />
      )}

      {/* AI Chat CLI Required Dialog */}
      <AiChatCliRequiredDialog
        open={showCliRequiredDialog}
        onOpenChange={setShowCliRequiredDialog}
      />

      {/* T13 — CLI Required Dialog for the "AI: Optimize this query" /
          "AI: Explain this error" task launchers. Kept separate from the
          dialog above (own hook instance, own state), matching the pattern
          `useAiTaskLauncher` establishes at every other call site. */}
      <AiChatCliRequiredDialog
        open={aiTaskCliRequiredOpen ?? false}
        onOpenChange={(open) => setAiTaskCliRequiredOpen?.(open)}
      />
    </>
  );
}
